import styled from "styled-components";
import ContentDrawer from "../ContentDrawer";
import { CloseDrawerButtonWithIcon } from "../ContentDrawer/DrawerComponents";
import { UIButton } from "../UIButton";
import { useEffect, useMemo, useState } from "react";
import ExistingTracksList from "../ExistingTracksList/ExistingTracksList";
import { ITrack } from "@src/types/track";
import TagFilterSelection from "../TagFilterSelection/TagFilterSelection";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import {
  resetTrackLibraryFilters,
  setMoodsFilter,
  setSearchTracksText,
  setTracksTagFilter,
} from "@src/redux/slices/moodFactorySlice";
import { SearchInput } from "../SearchInput";
import { useDebounce } from "@src/hooks/useDebounce";
import { Controller, useForm } from "react-hook-form";
import ErrorLabel from "../ErrorLabel";
import MoodsFilterSelection from "../MoodsFilterSelection/MoodsFilterSelection";
import { CircleActionButton } from "../CircleButton";
import FilterIcon from "@src/assets/img/filter_icon.svg";
import FilterIconActive from "@src/assets/img/filter-icon-active.svg";
import MoodTracksFilterForm from "./MoodTracksFilterForm";
import { useRole } from "@src/hooks/useAuth";
import { FEATURES } from "@src/constants";

type AddExistingTracksDrawerProps = {
  addTrackCallback: () => void;
  toggleDrawer: () => void;
  isOpen: boolean;
  playlistId: string;
  handlePlayButtonClick: (track: ITrack) => void;
};

const FilterButtonStyle: React.CSSProperties = {
  minWidth: "100px",
};

const AddExistingTracksDrawer = ({
  addTrackCallback,
  toggleDrawer,
  isOpen,
  playlistId,
  handlePlayButtonClick,
}: AddExistingTracksDrawerProps) => {
  const dispatch = useAppDispatch();
  const role = useRole();
  const libraryFiltersEnabled = FEATURES.MOOD_ADD_LIBRARY_FILTERS_ENABLED;
  const { currentPlayingTrack: trackSelected } = useAppSelector(
    (state) => state.trackPlayer
  );
  const {
    tracksTagFilter: { tags: selectedTags, excludedTags: selectedExcludedTags },
    moodsFilter: { moods: selectedMoods },
    trackLibraryFilters,
  } = useAppSelector((state) => state.moodFactory);
  const activeLibraryFiltersCount = useMemo(
    () =>
      [
        trackLibraryFilters.title,
        trackLibraryFilters.artist,
        trackLibraryFilters.album,
        trackLibraryFilters.label,
        trackLibraryFilters.key,
        trackLibraryFilters.bpm,
        trackLibraryFilters.durationInMinutes,
        trackLibraryFilters.isTopLikedThisWeek,
        trackLibraryFilters.missingYoutube,
        trackLibraryFilters.missingSpotify,
        trackLibraryFilters.missingApple,
        trackLibraryFilters.missingShazam,
        trackLibraryFilters.createdDateRange,
      ].filter(Boolean).length,
    [trackLibraryFilters]
  );
  const [isFilterTagsOpen, setIsFilterTagsOpen] = useState(false);
  const [isFilterMoodsOpen, setIsFilterMoodsOpen] = useState(false);
  const [isFilterLibraryOpen, setIsFilterLibraryOpen] = useState(false);
  const {
    control,
    watch,
    formState: { errors },
  } = useForm<{
    searchText: string;
  }>({
    defaultValues: { searchText: "" },
  });
  const searchText = useDebounce<string>(watch("searchText"), 300);
  useEffect(() => {
    dispatch(setTracksTagFilter({ tags: [], excludedTags: [] }));
    dispatch(setMoodsFilter({ moods: [], excludedMoods: [] }));
    dispatch(resetTrackLibraryFilters());
    return () => {
      dispatch(setTracksTagFilter({ tags: [], excludedTags: [] }));
      dispatch(setMoodsFilter({ moods: [], excludedMoods: [] }));
      dispatch(resetTrackLibraryFilters());
    };
  }, []);
  useEffect(() => {
    dispatch(setSearchTracksText(searchText));
    return () => {
      dispatch(setSearchTracksText(""));
    };
  }, [searchText]);

  const toggleActiveFilter = (filterType: "tags" | "moods" | "library") => {
    if (filterType === "tags") {
      setIsFilterTagsOpen(!isFilterTagsOpen);
      setIsFilterMoodsOpen(false);
      setIsFilterLibraryOpen(false);
    } else if (filterType === "moods") {
      setIsFilterMoodsOpen(!isFilterMoodsOpen);
      setIsFilterTagsOpen(false);
      setIsFilterLibraryOpen(false);
    } else if (filterType === "library") {
      setIsFilterLibraryOpen(!isFilterLibraryOpen);
      setIsFilterTagsOpen(false);
      setIsFilterMoodsOpen(false);
    }
  };

  return (
    <ContentDrawer
      noContentPadding
      drawerHeader={
        <TopButtonsGroup>
          <CloseDrawerButtonWithIcon disabled={false} onClick={toggleDrawer} />
          <div style={{ position: "relative" }}>
            <UIButton
              onClick={() => toggleActiveFilter("tags")}
              variant={isFilterTagsOpen ? "primary" : "primaryOutline"}
              style={FilterButtonStyle}
            >
              Filter Tags
            </UIButton>
            {selectedTags.length + selectedExcludedTags.length > 0 && (
              <BadgeContainer>
                <CounterBadge>
                  +{selectedTags.length + selectedExcludedTags.length}
                </CounterBadge>
              </BadgeContainer>
            )}
          </div>
          <div style={{ position: "relative" }}>
            <UIButton
              onClick={() => toggleActiveFilter("moods")}
              variant={isFilterMoodsOpen ? "primary" : "primaryOutline"}
              style={FilterButtonStyle}
            >
              Filter Moods
            </UIButton>
            {selectedMoods.length > 0 && (
              <BadgeContainer>
                <CounterBadge>+{selectedMoods.length}</CounterBadge>
              </BadgeContainer>
            )}
          </div>
          {libraryFiltersEnabled && (
            <div style={{ position: "relative" }}>
              <CircleActionButton
                $tooltipContent="Filter"
                $active={isFilterLibraryOpen}
                onClick={() => toggleActiveFilter("library")}
              >
                <img
                  src={isFilterLibraryOpen ? FilterIconActive : FilterIcon}
                  alt="filter"
                  style={{ width: "70%", objectFit: "contain" }}
                />
              </CircleActionButton>
              {activeLibraryFiltersCount > 0 && (
                <BadgeContainer>
                  <CounterBadge>+{activeLibraryFiltersCount}</CounterBadge>
                </BadgeContainer>
              )}
            </div>
          )}
          <div style={{ flex: 1, padding: 0 }}>
            {errors.searchText?.message && (
              <ErrorLabel>{errors.searchText?.message}</ErrorLabel>
            )}
            <Controller
              name="searchText"
              control={control}
              render={({ field }) => (
                <SearchInput
                  {...field}
                  onChange={field.onChange}
                  placeholder="Search by track name"
                />
              )}
            />
          </div>
        </TopButtonsGroup>
      }
      customHeight={
        trackSelected ? "calc(100vh - 379px)" : "calc(100vh - 212px)"
      }
      maxWidth="50vw"
      minWidth="50vw"
      isOpen={isOpen}
      toggleDrawer={toggleDrawer}
    >
      <FilterContainer
        shouldShowBottomPadding={isFilterTagsOpen || isFilterLibraryOpen}
      >
        <FiltersContent
          style={{ display: isFilterTagsOpen ? "block" : "none" }}
        >
          <TagFilterSelection key={playlistId} />
        </FiltersContent>
        <FiltersContent
          style={{ display: isFilterMoodsOpen ? "block" : "none" }}
        >
          <MoodsFilterSelection key={playlistId} />
        </FiltersContent>
        {libraryFiltersEnabled && (
          <FiltersContent
            style={{ display: isFilterLibraryOpen ? "block" : "none" }}
          >
            <MoodTracksFilterForm
              key={playlistId}
              role={role}
              closeButtonHandler={() => setIsFilterLibraryOpen(false)}
            />
          </FiltersContent>
        )}
      </FilterContainer>
      <ExistingTracksList
        addTrackCallback={addTrackCallback}
        playlistId={playlistId}
        handlePlayButtonClick={handlePlayButtonClick}
      />
    </ContentDrawer>
  );
};

const FilterContainer = styled.div<{ shouldShowBottomPadding?: boolean }>`
  padding-left: ${({ theme }) => theme.margins.medium};
  padding-right: ${({ theme }) => theme.margins.medium};
  padding-bottom: ${({ shouldShowBottomPadding = true, theme }) =>
    shouldShowBottomPadding ? theme.margins.medium : "0"};
`;

const FiltersContent = styled.div`
  background-color: ${({ theme }) => theme.colors.darkBg};
  padding: ${({ theme }) => theme.margins.medium};
`;

const TopButtonsGroup = styled.div`
  display: flex;
  width: 100%;
  gap: ${({ theme }) => theme.margins.medium};
`;
const BadgeContainer = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
`;

const CounterBadge = styled.span<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ active, theme }) =>
    active ? theme.colors.darkerBg : theme.colors.primary};
  color: ${({ active, theme }) =>
    active ? theme.colors.primary : theme.colors.darkBg};
  font-size: 0.7rem;
  padding: 2px;
  font-weight: bold;
  position: relative;
  top: -5px;
  right: -5px;
`;
export default AddExistingTracksDrawer;
